import React from 'react';
import { apiClient } from '../Services/api-client';

interface CreateTenantPayload {
  email: string;
  firstName: string;
  lastName: string;
  portfolioId: string;
  communityId: string;
}

interface CreateTenantResult {
  emailNorm: string;
  firstName?: string;
  lastName?: string;
  portfolioId: string;
  communityId: string;
}

export const useCreateTenant = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const createTenant = React.useCallback(
    async (payload: CreateTenantPayload): Promise<CreateTenantResult> => {
      const { email, firstName, lastName, portfolioId, communityId } = payload;
      setError(null);
      setLoading(true);

      try {
        try {
          const existing = await apiClient.get(
            `/tenant-access/tenants/${encodeURIComponent(email)}/grants`
          );
          const granted = existing.data?.granted?.communityAccess || [];
          const alreadyGranted = granted.some(
            (access: { portfolioId: string; communityId: string }) =>
              access.portfolioId === portfolioId && access.communityId === communityId
          );

          if (alreadyGranted) {
            throw new Error('Tenant already has access to this community.');
          }
        } catch (error: any) {
          const status = error?.response?.status;
          if (status !== 404) {
            const errorData = error?.response?.data;
            throw new Error(errorData?.error || 'Failed to validate existing access');
          }
        }

        const tenantPayload: { email: string; firstName?: string; lastName?: string } = {
          email,
        };

        if (firstName.trim()) tenantPayload.firstName = firstName.trim();
        if (lastName.trim()) tenantPayload.lastName = lastName.trim();

        await apiClient.post('/tenant-access', {
          tenant: tenantPayload,
          grant: { portfolioId, communityId },
        }, {
          headers: { 'Content-Type': 'application/json' },
        });

        return {
          emailNorm: email.toLowerCase(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          portfolioId,
          communityId,
        };
      } catch (err: any) {
        const message = err?.response?.data?.error || err?.message || 'Failed to create tenant';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearError = React.useCallback(() => setError(null), []);

  return { createTenant, loading, error, clearError };
};
